import React, { useCallback, useEffect, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import UserService from '../../services/UserService/UserService';
import User from '../../types/User';
import { snackbar } from './styles';
import PageTitleSection from '../../components/PageTitleSection/PageTitleSection';
import useAuthRoles from '../../hooks/useAuthRoles';
import InviteUserModal from './InviteUserModal/InviteUserModal';
import { HeaderDefinition } from '../../components/DataTable/DataTableHeader';
import { TableCell } from '../../components/ui/table';
import RenderUserRole from './RenderUserRole/RenderUserRole';
import RenderUserStatus from './RenderUserStatus/RenderUserStatus';
import SearchBar from '../../components/SearchBar/SearchBar';
import PageContentContainer from '../../components/PageContentContainer/PageContentContainer';
import DataTable from '../../components/DataTable/DataTable';
import Button from '../../components/Button/Button';
import useEffectiveOrganisationId from '../../hooks/useEffectiveOrganisationId';
import formatDate from '../../utils/formatDate';

function UsersPage(): React.ReactElement {
  const [query, setQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [numberOfResults, setNumberOfResults] = useState<number>(0);
  const [searchData, setSearchData] = useState<User[]>();
  const [searchIsLoading, setSearchIsLoading] = useState<boolean>(true);
  const [resultsPerPage, setResultsPerPage] = useState<number>(25);
  const [hasError, setHasError] = useState<boolean>(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState<boolean>(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] =
    useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [inviteUserModalOpen, setInviteUserModalOpen] =
    useState<boolean>(false);
  const onSuccessSnackbarClose = (): void => setShowSuccessSnackbar(false);
  const onErrorSnackbarClose = (): void => setShowErrorSnackbar(false);
  const [sortColumn, setSortColumn] = useState<
    'name' | 'email' | 'status' | 'authRole'
  >('name');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

  const openErrorSnackbar = (message: string): void => {
    setSnackbarMessage(message);
    setShowSuccessSnackbar(false);
    setShowErrorSnackbar(true);
  };

  const openSuccessSnackbar = (message: string): void => {
    setSnackbarMessage(message);
    setShowErrorSnackbar(false);
    setShowSuccessSnackbar(true);
  };

  const effectiveOrganisationId = useEffectiveOrganisationId();
  const { userIsAdmin } = useAuthRoles();
  const location = useLocation();
  const navigate = useNavigate();

  const getUsers = useCallback(
    (signal?: AbortSignal): void => {
      const optionalArgs = signal ? [signal] : [];
      setSearchIsLoading(true);
      UserService.getUsers({
        searchQuery: query,
        sortColumn,
        sortDirection,
        page,
        resultsPerPage,
        organisationId: effectiveOrganisationId,
        ...optionalArgs
      })
        .then((data) => {
          setHasError(false);
          setNumberOfResults(data.numberOfResults);
          setSearchData(data.results);
        })
        .catch((err) => {
          if (err.name === 'CanceledError') return;
          setNumberOfResults(0);
          setSearchData([]);
          setHasError(true);
        })
        .finally(() => setSearchIsLoading(false));
    },
    [
      page,
      resultsPerPage,
      query,
      effectiveOrganisationId,
      sortColumn,
      sortDirection
    ]
  );

  useEffect(() => {
    const controller = new AbortController();
    getUsers(controller.signal);
    return () => controller.abort();
  }, [getUsers]);

  const handleSortChange = useCallback(() => {
    getUsers();
  }, [getUsers]);

  if (!userIsAdmin()) {
    return <Navigate to='/chats' state={{ from: location }} replace />;
  }

  const conversationHeaders: HeaderDefinition[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      className: 'w-[22%]'
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      className: 'w-[28%]'
    },
    {
      key: 'authRole',
      label: 'Role',
      sortable: true,
      className: 'w-[15%]'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      className: 'w-[15%]'
    },
    {
      key: 'lastLoginAt',
      label: 'Last login',
      sortable: false,
      className: 'w-[20%]'
    }
  ];

  const renderUserRow = (userDetails: User) => (
    <>
      <TableCell className='w-[22%]'>
        <span className='block pr-2 font-medium'>
          {`${userDetails.firstName} ${userDetails.lastName ? userDetails.lastName : ''}`}
        </span>
      </TableCell>
      <TableCell className='w-[28%]'>
        <span className='block pr-2'>{userDetails.email}</span>
      </TableCell>
      <TableCell className='w-[15%]'>
        <RenderUserRole role={userDetails.role} />
      </TableCell>
      <TableCell className='w-[15%]'>
        <RenderUserStatus status={userDetails.status} />
      </TableCell>
      <TableCell className='w-[20%]'>
        <span className='block truncate pr-2'>
          {userDetails.lastLoginAt ? formatDate(userDetails.lastLoginAt) : '-'}
        </span>
      </TableCell>
    </>
  );

  const hasActiveSearch = query.trim().length > 0;
  const hasUsers = (searchData?.length ?? 0) > 0;
  const showInviteEmptyState =
    !searchIsLoading && !hasUsers && !hasActiveSearch;
  const showHeaderControls = hasActiveSearch || hasUsers;

  return (
    <>
      <Helmet>
        <title>Japeto - User Management</title>
      </Helmet>
      <PageTitleSection title='Users'>
        {showHeaderControls && (
          <Button
            icon={<UserPlus size='16' />}
            onClick={() => setInviteUserModalOpen(true)}
          >
            Invite user
          </Button>
        )}
      </PageTitleSection>
      <PageContentContainer className={showInviteEmptyState ? 'pt-16' : ''}>
        {showHeaderControls && (
          <SearchBar
            setQuery={setQuery}
            searchTermFromState={query}
            placeholder='Search users by name or email...'
          />
        )}
        <DataTable<User>
          searchData={searchData || null}
          numberOfResults={numberOfResults}
          resultsPerPage={resultsPerPage}
          isLoading={searchIsLoading}
          setResultsPerPage={setResultsPerPage}
          setPage={setPage}
          page={page}
          tableHeaders={conversationHeaders}
          renderRow={renderUserRow}
          sortColumn={sortColumn}
          setSortColumn={
            setSortColumn as React.Dispatch<React.SetStateAction<string>>
          }
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          onSortChange={handleSortChange}
          hasError={hasError}
          handleFetch={getUsers}
          hasActiveSearch={hasActiveSearch}
          noDataTitle='No users yet'
          noDataMessage='Invite the first user to get started.'
          noSearchResultsTitle='No users found'
          noSearchResultsMessage="There aren't any users matching your search."
          emptyStateAction={
            <Button
              icon={<UserPlus size='16' />}
              onClick={() => setInviteUserModalOpen(true)}
            >
              Invite user
            </Button>
          }
          isClickable
          handleRowClick={(user) => {
            navigate(`/users/${user.id}`);
          }}
        />
      </PageContentContainer>
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={onSuccessSnackbarClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        sx={snackbar}
      >
        <Alert
          onClose={onSuccessSnackbarClose}
          severity='success'
          variant='filled'
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={3000}
        onClose={onErrorSnackbarClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        sx={snackbar}
      >
        <Alert onClose={onErrorSnackbarClose} severity='error' variant='filled'>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <InviteUserModal
        open={inviteUserModalOpen}
        onClose={() => setInviteUserModalOpen(false)}
        openErrorSnackbar={openErrorSnackbar}
        openSuccessSnackbar={openSuccessSnackbar}
        onSuccess={getUsers}
      />
    </>
  );
}

export default UsersPage;
